<script setup lang="ts">
/**
 * Command palette (NS-3 / UX review backlog #5).
 *
 * Centered modal overlay opened with ⌘K / Ctrl+K. Reuses the searchStore
 * backend (same `/api/search` endpoint as the TopBar dropdown), but
 * presents results in a full-focus modal with keyboard-first navigation.
 *
 * Engineering notes:
 *   - Separate `commandPaletteStore` owns open state; searchStore owns
 *     query/results/recent (SSOT, CLAUDE_EP §Frontend Principles §1).
 *   - Flat-result flattening is shared via `useFlatSearchResults` so
 *     route shapes stay consistent with the TopBar dropdown.
 *   - data-qa-* on every interactive element per §Frontend Principles §6.
 *   - `cwr-motion` utilities per §3.1 motion pass / Lesson 7.
 */
import { computed, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { useSearchStore } from '@/stores/searchStore';
import { useFlatSearchResults } from '@/composables/useFlatSearchResults';
import { SeverityBadge } from '@/components/shared';
import { Search, Loader2, History, CornerDownLeft, X } from 'lucide-vue-next';

const router = useRouter();
const palette = useCommandPaletteStore();
const search = useSearchStore();
const { open } = storeToRefs(palette);
const { results, loading, selectedIndex, query, recent } = storeToRefs(search);

const flat = useFlatSearchResults(results);

const inputRef = ref<HTMLInputElement | null>(null);

// Autofocus input + reset selection each time the palette opens. Don't
// clear the query — a user reopening with stale results should see them
// instantly (matches VS Code / Linear behavior).
watch(open, (isOpen) => {
  if (isOpen) {
    selectedIndex.value = 0;
    nextTick(() => inputRef.value?.focus());
  }
});

// Typing proxies directly to the search store (debounce + abort lives there).
function onInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;
  search.runSearch(value);
}

const showRecent = computed(
  () => !results.value && !loading.value && query.value.length === 0 && recent.value.length > 0,
);
const showEmpty = computed(
  () => results.value !== null && flat.value.length === 0 && !loading.value,
);
const showResults = computed(() => flat.value.length > 0);

function closePalette() {
  palette.closePalette();
}

function onPick(index: number) {
  const pick = flat.value[index];
  if (!pick) return;
  search.rememberQuery(query.value);
  router.push(pick.route);
  closePalette();
}

function onRecentPick(q: string) {
  search.runSearch(q);
}

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) closePalette();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
    closePalette();
    return;
  }
  if (!showResults.value) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, flat.value.length - 1);
    scrollSelectedIntoView();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
    scrollSelectedIntoView();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    onPick(selectedIndex.value);
  }
}

function scrollSelectedIntoView() {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(
      '.command-palette-result[data-selected="true"]',
    );
    el?.scrollIntoView({ block: 'nearest' });
  });
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="cwr-palette-enter-active"
      leave-active-class="cwr-palette-leave-active"
      enter-from-class="cwr-palette-enter-from"
      leave-to-class="cwr-palette-leave-to"
    >
      <div
        v-if="open"
        class="command-palette-backdrop fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] bg-black/70"
        data-qa-command-palette-backdrop
        @click="onBackdropClick"
        @keydown="onKeydown"
      >
        <div
          class="command-palette-card w-full max-w-xl bg-surface-800 border border-border-default rounded-xl shadow-2xl overflow-hidden flex flex-col"
          role="dialog"
          aria-label="Command palette"
          data-qa-command-palette
        >
          <!-- Input row -->
          <div class="command-palette-input-row flex items-center gap-3 px-4 py-3 border-b border-border-default">
            <Search class="w-4 h-4 text-text-muted shrink-0" />
            <input
              ref="inputRef"
              :value="query"
              type="text"
              placeholder="Search actors, campaigns, rules, techniques…"
              autocomplete="off"
              spellcheck="false"
              class="command-palette-input flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-disabled outline-none"
              data-qa-command-palette-input
              @input="onInput"
            />
            <span class="command-palette-input-hint text-[10px] text-text-disabled px-1.5 py-0.5 rounded border border-border-default">
              Esc
            </span>
            <button
              type="button"
              class="command-palette-close p-1 rounded hover:bg-surface-700 text-text-muted cwr-motion"
              aria-label="Close palette"
              data-qa-command-palette-close
              @click="closePalette"
            >
              <X class="w-4 h-4" />
            </button>
          </div>

          <!-- Body -->
          <div class="command-palette-body max-h-[60vh] overflow-y-auto flex flex-col">
            <!-- Loading -->
            <div
              v-if="loading"
              class="flex items-center gap-2 px-4 py-3 text-sm text-text-muted"
              data-qa-command-palette-loading
            >
              <Loader2 class="w-4 h-4 animate-spin" />
              Searching…
            </div>

            <!-- Recent queries -->
            <div v-else-if="showRecent" class="flex flex-col">
              <div class="command-palette-section-header px-4 py-2 text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1.5 border-b border-border-default">
                <History class="w-3 h-3" />
                Recent
              </div>
              <button
                v-for="(r, idx) in recent"
                :key="`recent-${idx}`"
                class="command-palette-recent-item flex items-center gap-3 px-4 py-2 text-left text-sm text-text-secondary hover:bg-surface-700 cwr-motion"
                data-qa-command-palette-recent
                @mousedown.prevent="onRecentPick(r)"
              >
                <History class="w-3.5 h-3.5 text-text-muted shrink-0" />
                <span class="truncate">{{ r }}</span>
              </button>
            </div>

            <!-- Empty state -->
            <div
              v-else-if="showEmpty"
              class="command-palette-empty flex flex-col items-center justify-center gap-1 px-4 py-8 text-center"
              data-qa-command-palette-empty
            >
              <Search class="w-8 h-8 text-text-disabled opacity-50" />
              <span class="text-sm text-text-muted">No results for "{{ query }}"</span>
            </div>

            <!-- Results -->
            <div
              v-else-if="showResults"
              class="command-palette-results flex flex-col"
            >
              <button
                v-for="(r, idx) in flat"
                :key="r.id"
                :data-selected="idx === selectedIndex"
                :class="[
                  'command-palette-result flex items-center gap-3 px-4 py-2 text-left cwr-motion',
                  idx === selectedIndex ? 'bg-surface-700' : 'hover:bg-surface-750',
                ]"
                data-qa-command-palette-result
                @mousedown.prevent="onPick(idx)"
                @mouseenter="selectedIndex = idx"
              >
                <component :is="r.groupIcon" class="command-palette-result-icon w-4 h-4 text-text-muted shrink-0" />
                <span class="command-palette-result-group text-[10px] uppercase tracking-wider text-text-muted w-24 shrink-0">
                  {{ r.group }}
                </span>
                <span class="command-palette-result-label flex-1 text-sm text-text-primary truncate">
                  {{ r.label }}
                </span>
                <span v-if="r.sublabel" class="command-palette-result-sublabel text-xs text-text-muted capitalize shrink-0">
                  {{ r.sublabel }}
                </span>
                <SeverityBadge v-if="r.severity" :severity="(r.severity as any)" />
              </button>
            </div>

            <!-- Empty-query hint -->
            <div
              v-else
              class="command-palette-hint flex flex-col items-center justify-center gap-1 px-4 py-8 text-center"
              data-qa-command-palette-hint
            >
              <Search class="w-8 h-8 text-text-disabled opacity-50" />
              <span class="text-sm text-text-muted">Start typing to search</span>
              <span class="text-[10px] text-text-disabled">Actors · Campaigns · Indicators · Rules · Reports · MITRE</span>
            </div>
          </div>

          <!-- Footer shortcut hints -->
          <div class="command-palette-footer flex items-center gap-4 px-4 py-2 border-t border-border-default text-[10px] text-text-disabled">
            <span class="flex items-center gap-1">
              <span class="command-palette-kbd px-1 py-0.5 rounded border border-border-default font-mono">↑↓</span>
              navigate
            </span>
            <span class="flex items-center gap-1">
              <CornerDownLeft class="w-3 h-3" />
              select
            </span>
            <span class="flex items-center gap-1">
              <span class="command-palette-kbd px-1 py-0.5 rounded border border-border-default font-mono">Esc</span>
              close
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cwr-palette-enter-active,
.cwr-palette-leave-active {
  transition: opacity 150ms ease-out;
}
.cwr-palette-enter-active .command-palette-card,
.cwr-palette-leave-active .command-palette-card {
  transition: transform 150ms ease-out, opacity 150ms ease-out;
}
.cwr-palette-enter-from,
.cwr-palette-leave-to {
  opacity: 0;
}
.cwr-palette-enter-from .command-palette-card,
.cwr-palette-leave-to .command-palette-card {
  transform: translateY(-8px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .cwr-palette-enter-active,
  .cwr-palette-leave-active,
  .cwr-palette-enter-active .command-palette-card,
  .cwr-palette-leave-active .command-palette-card {
    transition-duration: 0ms !important;
  }
}
</style>
